import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Header, Page, Content } from '@backstage/core-components';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import DeleteIcon from '@material-ui/icons/Delete';
import StorageIcon from '@material-ui/icons/Storage';
import AssessmentIcon from '@material-ui/icons/Assessment';
import CodeIcon from '@material-ui/icons/Code';
import BugReportIcon from '@material-ui/icons/BugReport';

// ─── Types ────────────────────────────────────────────────────────────────────

type RunRecord = {
  id: string;
  repo_name: string;
  runner_id: string;
  workflow_run_number: number | null;
  jira_ticket_id: string;
  generation_status: 'success' | 'no_changes' | 'error';
  has_changes: boolean | null;
  branch_name: string | null;
  branch_url: string | null;
  pr_number: number | null;
  pr_url: string | null;
  pr_action: string | null;
  target_branch: string | null;
  model_name: string | null;
  ai_provider: string | null;
  ai_cost: number | null;
  confluence_url: string | null;
  pr_creator: string | null;
  run_timestamp: string;
  is_error: boolean;
  error_message: string | null;
  error_step: string | null;
  job_status: string | null;
  created_at: string;
};

type RepoSummary = {
  repoName: string;
  totalRuns: number;
  successCount: number;
  noChangesCount: number;
  errorCount: number;
  totalCost: number | null;
  lastRun: string;
  latestStatus: string | null;
  latestIsError: boolean;
  latestBranch: string | null;
  latestModel: string | null;
  latestJobStatus: string | null;
};

type Stats = {
  total_runs: number;
  successful_generations: number;
  no_changes_count: number;
  errors_count: number;
  total_prs_created: number;
  total_ai_cost: number | null;
  success_rate: string;
  avg_cost_per_run: number | null;
  by_repo: { repo_name: string; count: number; success_rate: string }[];
  by_ai_provider: { provider: string; count: number; total_cost: number | null }[];
  recent_errors: RunRecord[];
};

type TrendPoint = {
  id: string;
  repo_name: string;
  generation_status: string;
  has_changes: boolean | null;
  branch_name: string | null;
  pr_number: number | null;
  pr_action: string | null;
  model_name: string | null;
  ai_provider: string | null;
  ai_cost: number | null;
  pr_creator: string | null;
  run_timestamp: string;
  workflow_run_number: number | null;
  is_error: boolean;
  error_step: string | null;
  job_status: string | null;
  created_at: string;
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  statCard: {
    padding: theme.spacing(2, 3),
    textAlign: 'center',
    borderRadius: 10,
    height: '100%',
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 800,
    lineHeight: 1.2,
    color: theme.palette.text.primary,
  },
  statLabel: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
    marginTop: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  errorRow: {
    backgroundColor: '#FFF8F8',
    '&:hover': { backgroundColor: '#FFECEC' },
  },
  alarmPulse: {
    animation: '$pulse 1.4s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.35 },
    '100%': { opacity: 1 },
  },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

function statusColor(status: string | null, isError?: boolean): string {
  if (isError) return '#F44336';
  switch (status) {
    case 'success': return '#4CAF50';
    case 'no_changes': return '#FF9800';
    case 'error': return '#F44336';
    default: return '#9E9E9E';
  }
}

function StatusBadge({ status, isError }: { status: string | null; isError?: boolean }) {
  const color = statusColor(status, isError);
  const label = isError && status !== 'error' ? 'error' : (status ?? '—');
  const icon =
    label === 'success' ? <CheckCircleIcon style={{ fontSize: 13 }} /> :
    label === 'no_changes' ? <RemoveCircleOutlineIcon style={{ fontSize: 13 }} /> :
    label === 'error' ? <CancelIcon style={{ fontSize: 13 }} /> : undefined;

  return (
    <Chip
      label={label.replace('_', ' ')}
      icon={icon}
      size="small"
      style={{
        fontSize: '0.68rem',
        height: 20,
        backgroundColor: color,
        color: '#fff',
        fontWeight: 600,
      }}
    />
  );
}

function formatDate(ts: string): string {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function shortName(repoName: string): string {
  const slash = repoName.indexOf('/');
  return slash !== -1 ? repoName.slice(slash + 1) : repoName;
}

function UserLink({ login }: { login: string }) {
  return (
    <a
      href={`/catalog/default/user/${login}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none', color: 'inherit' }}
      onMouseOver={e => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline'; }}
      onMouseOut={e => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none'; }}
    >
      {login}
    </a>
  );
}

// ─── Run Details View ─────────────────────────────────────────────────────────

function RunDetailView({
  run,
  onBack,
  baseUrl,
  fetchApi,
}: {
  run: RunRecord;
  onBack: () => void;
  baseUrl: string;
  fetchApi: any;
}) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleDelete = async () => {
    setDeletingId(run.id);
    await fetchApi.fetch(`${baseUrl}/run/${run.id}`, { method: 'DELETE' });
    onBack();
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2} style={{ gap: 12 }}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          Run #{run.workflow_run_number ?? run.runner_id.slice(0, 8)}
        </Typography>
        <StatusBadge status={run.generation_status} isError={run.is_error} />
        <Box style={{ flex: 1 }} />
        <Tooltip title="Delete this run">
          <IconButton size="small" disabled={!!deletingId} onClick={handleDelete}>
            <DeleteIcon style={{ fontSize: 18, color: deletingId ? '#ccc' : '#D32F2F' }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2}>
        {/* Run Overview */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} style={{ padding: 20 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
              Run Overview
            </Typography>
            {[
              { label: 'Repository', value: run.repo_name },
              { label: 'JIRA Ticket', value: run.jira_ticket_id },
              { label: 'Run Number', value: run.workflow_run_number ? `#${run.workflow_run_number}` : '—' },
              { label: 'Runner ID', value: run.runner_id },
              { label: 'Timestamp', value: formatDate(run.run_timestamp) },
              { label: 'Target Branch', value: run.target_branch ?? '—' },
              {
                label: 'PR Creator',
                value: run.pr_creator ? <UserLink login={run.pr_creator} /> : '—',
              },
            ].map(({ label, value }) => (
              <Box key={label} display="flex" style={{ marginBottom: 6 }}>
                <Typography variant="body2" color="textSecondary" style={{ minWidth: 130 }}>{label}:</Typography>
                <Typography variant="body2" style={{ fontWeight: 500 }}>{value}</Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Generation Results */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} style={{ padding: 20 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
              Generation Results
            </Typography>
            <Box display="flex" style={{ marginBottom: 6 }}>
              <Typography variant="body2" color="textSecondary" style={{ minWidth: 130 }}>Has Changes:</Typography>
              <Box>
                {run.has_changes === true && <CheckCircleIcon style={{ fontSize: 16, color: '#4CAF50' }} />}
                {run.has_changes === false && <CancelIcon style={{ fontSize: 16, color: '#F44336' }} />}
                {run.has_changes === null && <Typography variant="body2">—</Typography>}
              </Box>
            </Box>
            {[
              { label: 'Branch', value: run.branch_name, link: run.branch_url },
              { label: 'PR Number', value: run.pr_number ? `#${run.pr_number}` : null, link: run.pr_url },
              { label: 'PR Action', value: run.pr_action },
            ].map(({ label, value, link }) => (
              <Box key={label} display="flex" style={{ marginBottom: 6 }}>
                <Typography variant="body2" color="textSecondary" style={{ minWidth: 130 }}>{label}:</Typography>
                {value && link ? (
                  <a href={link} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {value} <OpenInNewIcon style={{ fontSize: 12 }} />
                  </a>
                ) : (
                  <Typography variant="body2" style={{ fontWeight: 500 }}>{value ?? '—'}</Typography>
                )}
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* AI Usage */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} style={{ padding: 20 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
              AI Usage
            </Typography>
            {[
              { label: 'Model', value: run.model_name },
              { label: 'Provider', value: run.ai_provider },
              { label: 'Cost', value: run.ai_cost !== null ? `$${Number(run.ai_cost).toFixed(4)}` : null },
            ].map(({ label, value }) => (
              <Box key={label} display="flex" style={{ marginBottom: 6 }}>
                <Typography variant="body2" color="textSecondary" style={{ minWidth: 130 }}>{label}:</Typography>
                <Typography variant="body2" style={{ fontFamily: 'monospace', fontWeight: 500 }}>{value ?? '—'}</Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Links */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} style={{ padding: 20 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
              Links
            </Typography>
            {[
              { label: 'GitHub Branch', href: run.branch_url },
              { label: 'GitHub PR', href: run.pr_url },
              { label: 'Confluence Report', href: run.confluence_url },
            ].filter(l => l.href).map(({ label, href }) => (
              <Box key={label} mb={1}>
                <a href={href!} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.875rem' }}>
                  {label} <OpenInNewIcon style={{ fontSize: 13 }} />
                </a>
              </Box>
            ))}
            {!run.branch_url && !run.pr_url && !run.confluence_url && (
              <Typography variant="body2" color="textSecondary">No links available</Typography>
            )}
          </Paper>
        </Grid>

        {/* Error Details */}
        {run.is_error && (
          <Grid item xs={12}>
            <Paper elevation={1} style={{ padding: 20, borderLeft: '4px solid #D32F2F' }}>
              <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 12 }}>
                <BugReportIcon style={{ color: '#D32F2F' }} />
                <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#B71C1C' }}>
                  Error Details
                </Typography>
              </Box>
              {run.error_step && (
                <Box display="flex" style={{ marginBottom: 8 }}>
                  <Typography variant="body2" color="textSecondary" style={{ minWidth: 130 }}>Step:</Typography>
                  <Chip label={run.error_step} size="small"
                    style={{ fontSize: '0.68rem', height: 20, backgroundColor: '#FFF3E0', color: '#E65100', border: '1px solid #FFB74D' }} />
                </Box>
              )}
              {run.job_status && (
                <Box display="flex" style={{ marginBottom: 8 }}>
                  <Typography variant="body2" color="textSecondary" style={{ minWidth: 130 }}>Job Status:</Typography>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>{run.job_status}</Typography>
                </Box>
              )}
              {run.error_message && (
                <Box>
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>Message:</Typography>
                  <Box
                    component="pre"
                    style={{
                      background: '#FFF8F8',
                      border: '1px solid #FFCDD2',
                      borderRadius: 6,
                      padding: 12,
                      fontSize: '0.8rem',
                      color: '#D32F2F',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      margin: 0,
                    }}
                  >
                    {run.error_message}
                  </Box>
                </Box>
              )}
              <Box mt={2} p={1.5} style={{ background: '#F3F4F6', borderRadius: 6 }}>
                <Typography variant="caption" color="textSecondary">
                  <strong>Troubleshooting tips:</strong> Check the GitHub Actions logs for run #{run.workflow_run_number ?? run.runner_id}.
                  Common causes: AI API rate limits, missing test files, or branch permission issues.
                </Typography>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

// ─── Daily Trend Chart ────────────────────────────────────────────────────────

type DailyBucket = {
  date: string;       // "Mar 14"
  isoDate: string;    // "2026-03-14" for sorting
  total: number;
  success: number;
  no_changes: number;
  errors: number;
  cost: number;
};

function buildDailyBuckets(trends: TrendPoint[], days: number): DailyBucket[] {
  const buckets: Record<string, DailyBucket> = {};
  const now = new Date();

  // Pre-fill every day in range so gaps show as zero
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const iso = d.toISOString().slice(0, 10);
    const label = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    buckets[iso] = { date: label, isoDate: iso, total: 0, success: 0, no_changes: 0, errors: 0, cost: 0 };
  }

  for (const t of trends) {
    const iso = (t.created_at || t.run_timestamp || '').slice(0, 10);
    if (!buckets[iso]) continue;
    buckets[iso].total += 1;
    if (t.is_error) buckets[iso].errors += 1;
    else if (t.generation_status === 'success') buckets[iso].success += 1;
    else if (t.generation_status === 'no_changes') buckets[iso].no_changes += 1;
    if (t.ai_cost) buckets[iso].cost += Number(t.ai_cost);
  }

  return Object.values(buckets).sort((a, b) => a.isoDate.localeCompare(b.isoDate));
}

function DailyTrendChart({
  baseUrl,
  fetchApi,
  days,
}: {
  baseUrl: string;
  fetchApi: any;
  days: number;
}) {
  const [trends, setTrends] = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [showCost, setShowCost] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchApi.fetch(`${baseUrl}/trends?limit=200`)
      .then((r: any) => r.ok ? r.json() : [])
      .then((data: TrendPoint[]) => { setTrends(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [baseUrl, fetchApi, days]);

  const buckets = buildDailyBuckets(trends, days);

  // Chart dimensions
  const W = 900, H = 220, PAD = { top: 16, right: 20, bottom: 36, left: 42 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const maxY = showCost
    ? Math.max(...buckets.map(b => b.cost), 0.001)
    : Math.max(...buckets.map(b => b.total), 1);

  const xStep = buckets.length > 1 ? chartW / (buckets.length - 1) : chartW;

  const xOf = (i: number) => PAD.left + i * xStep;
  const yOf = (v: number) => PAD.top + chartH - (v / maxY) * chartH;

  const lineFor = (key: keyof DailyBucket, color: string) => {
    if (buckets.length === 0) return null;
    const pts = buckets.map((b, i) => `${xOf(i)},${yOf(Number(b[key]))}`).join(' ');
    return <polyline points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />;
  };

  const areaFor = (key: keyof DailyBucket, color: string) => {
    if (buckets.length === 0) return null;
    const top = buckets.map((b, i) => `${xOf(i)},${yOf(Number(b[key]))}`).join(' ');
    const base = `${xOf(buckets.length - 1)},${yOf(0)} ${xOf(0)},${yOf(0)}`;
    return <polygon points={`${top} ${base}`} fill={color} fillOpacity={0.08} />;
  };

  // Y-axis ticks
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({
    v: f * maxY,
    y: yOf(f * maxY),
    label: showCost ? `$${(f * maxY).toFixed(2)}` : String(Math.round(f * maxY)),
  }));

  // X-axis: show at most 10 labels evenly spaced
  const labelStep = Math.max(1, Math.ceil(buckets.length / 10));

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>;
  if (buckets.every(b => b.total === 0)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={100}>
        <Typography variant="body2" color="textSecondary">No runs in this time range</Typography>
      </Box>
    );
  }

  const LINES = showCost
    ? [{ key: 'cost' as const, color: '#1a73e8', label: 'AI Cost ($)' }]
    : [
        { key: 'total' as const, color: '#1a73e8', label: 'Total' },
        { key: 'success' as const, color: '#4CAF50', label: 'Success' },
        { key: 'no_changes' as const, color: '#FF9800', label: 'No Changes' },
        { key: 'errors' as const, color: '#F44336', label: 'Errors' },
      ];

  const active = activeIdx !== null ? buckets[activeIdx] : null;

  return (
    <Box>
      {/* Legend + toggle */}
      <Box display="flex" alignItems="center" mb={1} style={{ gap: 16, flexWrap: 'wrap' }}>
        {LINES.map(l => (
          <Box key={l.key} display="flex" alignItems="center" style={{ gap: 5 }}>
            <span style={{ display: 'inline-block', width: 20, height: 3, borderRadius: 2, backgroundColor: l.color }} />
            <Typography variant="caption" color="textSecondary">{l.label}</Typography>
          </Box>
        ))}
        <Box style={{ marginLeft: 'auto' }}>
          <Chip
            label={showCost ? 'Show Runs' : 'Show Cost'}
            size="small"
            clickable
            onClick={() => setShowCost(v => !v)}
            style={{ fontSize: '0.68rem', height: 20 }}
          />
        </Box>
      </Box>

      {/* SVG chart */}
      <Box style={{ overflowX: 'auto' }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          style={{ width: '100%', maxWidth: W, display: 'block' }}
          onMouseLeave={() => setActiveIdx(null)}
        >
          {/* Grid lines */}
          {yTicks.map((t, i) => (
            <g key={i}>
              <line x1={PAD.left} y1={t.y} x2={W - PAD.right} y2={t.y}
                stroke="#E0E0E0" strokeWidth={1} strokeDasharray={i === 0 ? '0' : '4 3'} />
              <text x={PAD.left - 6} y={t.y + 4} textAnchor="end"
                style={{ fontSize: 10, fill: '#9E9E9E', fontFamily: 'monospace' }}>
                {t.label}
              </text>
            </g>
          ))}

          {/* Areas */}
          {LINES.map(l => areaFor(l.key, l.color))}

          {/* Lines */}
          {LINES.map(l => lineFor(l.key, l.color))}

          {/* X-axis labels */}
          {buckets.map((b, i) => i % labelStep === 0 && (
            <text key={i} x={xOf(i)} y={H - 6} textAnchor="middle"
              style={{ fontSize: 10, fill: '#9E9E9E', fontFamily: 'sans-serif' }}>
              {b.date}
            </text>
          ))}

          {/* Hover vertical bands */}
          {buckets.map((b, i) => {
            const bw = Math.max(xStep, 8);
            return (
              <rect
                key={i}
                x={xOf(i) - bw / 2}
                y={PAD.top}
                width={bw}
                height={chartH}
                fill="transparent"
                onMouseEnter={() => setActiveIdx(i)}
              />
            );
          })}

          {/* Active dot + crosshair */}
          {activeIdx !== null && (
            <>
              <line x1={xOf(activeIdx)} y1={PAD.top} x2={xOf(activeIdx)} y2={PAD.top + chartH}
                stroke="#9E9E9E" strokeWidth={1} strokeDasharray="3 2" />
              {LINES.map(l => {
                const v = Number(buckets[activeIdx][l.key]);
                return (
                  <circle key={l.key} cx={xOf(activeIdx)} cy={yOf(v)} r={4}
                    fill={l.color} stroke="#fff" strokeWidth={2} />
                );
              })}
            </>
          )}
        </svg>
      </Box>

      {/* Tooltip / hover info */}
      <Box style={{ minHeight: 36 }}>
        {active && (
          <Box display="flex" alignItems="center" style={{ gap: 16, flexWrap: 'wrap', paddingLeft: 8 }}>
            <Typography variant="caption" style={{ fontWeight: 700, color: '#555' }}>{active.date}:</Typography>
            {showCost ? (
              <Typography variant="caption" style={{ color: '#1a73e8', fontFamily: 'monospace' }}>
                ${active.cost.toFixed(4)}
              </Typography>
            ) : (
              <>
                <Typography variant="caption" style={{ color: '#1a73e8' }}>Total: <strong>{active.total}</strong></Typography>
                <Typography variant="caption" style={{ color: '#4CAF50' }}>Success: <strong>{active.success}</strong></Typography>
                <Typography variant="caption" style={{ color: '#FF9800' }}>No Changes: <strong>{active.no_changes}</strong></Typography>
                <Typography variant="caption" style={{ color: '#F44336' }}>Errors: <strong>{active.errors}</strong></Typography>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── Repo History Panel ───────────────────────────────────────────────────────

function RepoHistoryPanel({
  repoName,
  baseUrl,
  fetchApi,
  onBack,
  onSelectRun,
}: {
  repoName: string;
  baseUrl: string;
  fetchApi: any;
  onBack: () => void;
  onSelectRun: (run: RunRecord) => void;
}) {
  const classes = useStyles();
  const [runs, setRuns] = useState<RunRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [subTab, setSubTab] = useState(0);
  const [confirmDeleteRepo, setConfirmDeleteRepo] = useState(false);
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchApi.fetch(`${baseUrl}/repo/${encodeURIComponent(repoName)}?limit=100`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRuns(await res.json());
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [repoName, baseUrl, fetchApi]);

  useEffect(() => { load(); }, [load]);

  const handleDeleteRepo = async () => {
    await fetchApi.fetch(`${baseUrl}/repo/${encodeURIComponent(repoName)}`, { method: 'DELETE' });
    onBack();
  };

  const handleDeleteRun = async (id: string) => {
    setDeletingRunId(id);
    await fetchApi.fetch(`${baseUrl}/run/${id}`, { method: 'DELETE' });
    setRuns(prev => prev.filter(r => r.id !== id));
    setDeletingRunId(null);
  };

  const q = search.trim().toLowerCase();
  const successRuns = runs.filter(r => !r.is_error);
  const errorRuns = runs.filter(r => r.is_error);

  const filteredSuccess = successRuns.filter(r => !q ||
    (r.jira_ticket_id ?? '').toLowerCase().includes(q) ||
    (r.branch_name ?? '').toLowerCase().includes(q) ||
    (r.pr_creator ?? '').toLowerCase().includes(q) ||
    String(r.pr_number ?? '').includes(q) ||
    String(r.workflow_run_number ?? '').includes(q),
  );

  const filteredErrors = errorRuns.filter(r => !q ||
    (r.error_step ?? '').toLowerCase().includes(q) ||
    (r.error_message ?? '').toLowerCase().includes(q) ||
    String(r.workflow_run_number ?? '').includes(q),
  );

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2} style={{ gap: 12 }}>
        <IconButton size="small" onClick={onBack}><ArrowBackIcon /></IconButton>
        <Typography variant="h6" style={{ fontWeight: 700 }}>{shortName(repoName)}</Typography>
        <IconButton size="small" onClick={load} title="Refresh"><RefreshIcon /></IconButton>
        <Box style={{ flex: 1 }} />
        {confirmDeleteRepo ? (
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Typography variant="body2" color="error">Delete all runs?</Typography>
            <Button size="small" variant="contained" style={{ backgroundColor: '#D32F2F', color: '#fff' }} onClick={handleDeleteRepo}>Confirm</Button>
            <Button size="small" onClick={() => setConfirmDeleteRepo(false)}>Cancel</Button>
          </Box>
        ) : (
          <Tooltip title="Delete all runs for this repo">
            <IconButton size="small" onClick={() => setConfirmDeleteRepo(true)}>
              <DeleteIcon style={{ color: '#D32F2F' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {loading && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}
      {error && <Typography color="error">{error}</Typography>}

      {!loading && !error && (
        <>
          {/* Active error banner */}
          {runs.length > 0 && runs[0].is_error && (
            <Paper elevation={0} style={{ background: '#FFEBEE', border: '1px solid #FFCDD2', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
              <WarningIcon style={{ color: '#D32F2F', fontSize: 20 }} />
              <Box>
                <Typography variant="body2" style={{ fontWeight: 700, color: '#B71C1C' }}>Active Error — last run failed</Typography>
                <Typography variant="caption" color="textSecondary">
                  {runs[0].error_step ? `Step: ${runs[0].error_step} · ` : ''}{runs[0].error_message ?? 'No error message recorded'}
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Sub-tabs */}
          <Box display="flex" alignItems="center" mb={1} style={{ gap: 12, flexWrap: 'wrap' }}>
            <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} indicatorColor="primary" textColor="primary">
              <Tab label={`Runs (${successRuns.length})`} style={{ minWidth: 0, fontSize: '0.82rem' }} />
              <Tab
                label={`Errors (${errorRuns.length})`}
                style={{ minWidth: 0, fontSize: '0.82rem', color: errorRuns.length > 0 ? '#D32F2F' : undefined }}
              />
            </Tabs>
            <Box style={{ flex: 1, minWidth: 180 }}>
              <TextField
                size="small" variant="outlined" placeholder="Search Jira, branch, PR#…"
                value={search} onChange={e => setSearch(e.target.value)} fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 18, color: '#999' }} /></InputAdornment>,
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')}><ClearIcon style={{ fontSize: 16 }} /></IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            </Box>
          </Box>

          {subTab === 0 && (
            <TableContainer component={Paper} elevation={1}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Run</strong></TableCell>
                    <TableCell><strong>JIRA</strong></TableCell>
                    <TableCell><strong>Branch</strong></TableCell>
                    <TableCell><strong>PR</strong></TableCell>
                    <TableCell><strong>Creator</strong></TableCell>
                    <TableCell><strong>Model</strong></TableCell>
                    <TableCell><strong>AI Cost</strong></TableCell>
                    <TableCell><strong>Timestamp</strong></TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSuccess.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="textSecondary">
                          {successRuns.length === 0 ? 'No runs recorded yet' : 'No runs match your search'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredSuccess.map(r => (
                    <TableRow key={r.id} className={classes.tableRow} onClick={() => onSelectRun(r)}>
                      <TableCell>
                        <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          #{r.workflow_run_number ?? r.runner_id.slice(0, 8)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 600 }}>
                          {r.jira_ticket_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {r.branch_name ? (
                          r.branch_url ? (
                            <a href={r.branch_url}
                              target="_blank" rel="noopener noreferrer"
                              style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                              onClick={e => e.stopPropagation()}>
                              {r.branch_name}
                            </a>
                          ) : (
                            <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {r.branch_name}
                            </Typography>
                          )
                        ) : <Typography variant="body2" color="textSecondary">—</Typography>}
                      </TableCell>
                      <TableCell>
                        {r.pr_number ? (
                          r.pr_url ? (
                            <a href={r.pr_url}
                              target="_blank" rel="noopener noreferrer"
                              style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
                              onClick={e => e.stopPropagation()}>
                              #{r.pr_number}
                            </a>
                          ) : (
                            <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                              #{r.pr_number}
                            </Typography>
                          )
                        ) : <Typography variant="body2" color="textSecondary">—</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontSize: '0.78rem' }}>
                          {r.pr_creator ? <UserLink login={r.pr_creator} /> : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          {r.model_name ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          {r.ai_cost !== null ? `$${Number(r.ai_cost).toFixed(4)}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary" style={{ whiteSpace: 'nowrap' }}>
                          {formatDate(r.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell padding="none">
                        <Tooltip title="Delete this run">
                          <IconButton size="small" disabled={deletingRunId === r.id}
                            onClick={e => { e.stopPropagation(); handleDeleteRun(r.id); }}>
                            <DeleteIcon style={{ fontSize: 16, color: deletingRunId === r.id ? '#ccc' : '#D32F2F' }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {subTab === 1 && (
            <Box>
              {filteredErrors.length === 0 && (
                <Paper elevation={1} style={{ padding: 24, textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    {errorRuns.length === 0 ? 'No errors recorded' : 'No errors match your search'}
                  </Typography>
                </Paper>
              )}
              {filteredErrors.map(r => (
                <Paper key={r.id} elevation={1} style={{ padding: '12px 16px', marginBottom: 10, borderLeft: '4px solid #D32F2F', cursor: 'pointer' }}
                  onClick={() => onSelectRun(r)}>
                  <Box display="flex" alignItems="flex-start" style={{ gap: 12 }}>
                    <Box style={{ flex: 1 }}>
                      <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                        <Typography variant="body2" style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem' }}>
                          #{r.workflow_run_number ?? r.runner_id.slice(0, 8)}
                        </Typography>
                        <Chip label={r.jira_ticket_id} size="small" style={{ fontSize: '0.65rem', height: 18 }} />
                        {r.error_step && (
                          <Chip label={r.error_step} size="small"
                            style={{ fontSize: '0.65rem', height: 18, backgroundColor: '#FFF3E0', color: '#E65100', border: '1px solid #FFB74D' }} />
                        )}
                        <Typography variant="caption" color="textSecondary" style={{ marginLeft: 'auto' }}>
                          {formatDate(r.created_at)}
                        </Typography>
                      </Box>
                      {r.error_message && (
                        <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#D32F2F', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {r.error_message}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title="Delete this record">
                      <IconButton size="small" disabled={deletingRunId === r.id}
                        onClick={e => { e.stopPropagation(); handleDeleteRun(r.id); }}>
                        <DeleteIcon style={{ fontSize: 16, color: deletingRunId === r.id ? '#ccc' : '#D32F2F' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// ─── Jira Ticket View ─────────────────────────────────────────────────────────

function JiraTicketView({
  ticketId,
  baseUrl,
  fetchApi,
  onBack,
  onSelectRun,
}: {
  ticketId: string;
  baseUrl: string;
  fetchApi: any;
  onBack: () => void;
  onSelectRun: (run: RunRecord) => void;
}) {
  const [runs, setRuns] = useState<RunRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchApi.fetch(`${baseUrl}/runs/jira/${encodeURIComponent(ticketId)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRuns(await res.json());
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [ticketId, baseUrl, fetchApi]);

  useEffect(() => { load(); }, [load]);

  const totalCost = runs.reduce((sum, r) => sum + (r.ai_cost ? Number(r.ai_cost) : 0), 0);
  const latestRun = runs[0] ?? null;

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2} style={{ gap: 12 }}>
        <IconButton size="small" onClick={onBack}><ArrowBackIcon /></IconButton>
        <Typography variant="h6" style={{ fontWeight: 700 }}>JIRA: {ticketId}</Typography>
        {latestRun && <StatusBadge status={latestRun.generation_status} isError={latestRun.is_error} />}
        <IconButton size="small" onClick={load} title="Refresh"><RefreshIcon /></IconButton>
      </Box>

      {loading && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}
      {error && <Typography color="error">{error}</Typography>}

      {!loading && !error && (
        <>
          {/* Summary cards */}
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            {[
              { label: 'Total Attempts', value: runs.length },
              { label: 'Successful', value: runs.filter(r => r.generation_status === 'success').length, color: '#4CAF50' },
              { label: 'No Changes', value: runs.filter(r => r.generation_status === 'no_changes').length, color: '#FF9800' },
              { label: 'Errors', value: runs.filter(r => r.is_error).length, color: runs.some(r => r.is_error) ? '#F44336' : '#4CAF50' },
              { label: 'Total Cost', value: totalCost > 0 ? `$${totalCost.toFixed(4)}` : '—' },
            ].map(s => (
              <Grid item xs={6} sm key={s.label}>
                <Paper elevation={1} style={{ padding: '12px 16px', textAlign: 'center', borderRadius: 8 }}>
                  <Typography style={{ fontSize: '1.6rem', fontWeight: 800, color: (s as any).color ?? 'inherit' }}>
                    {String(s.value)}
                  </Typography>
                  <Typography style={{ fontSize: '0.7rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {s.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Timeline */}
          <Typography style={{ fontWeight: 700, marginBottom: 12 }}>Generation Timeline</Typography>
          {runs.length === 0 && (
            <Paper elevation={1} style={{ padding: 24, textAlign: 'center' }}>
              <Typography color="textSecondary">No runs found for this ticket</Typography>
            </Paper>
          )}
          {runs.map((r, i) => (
            <Box key={r.id} display="flex" style={{ marginBottom: 12 }}>
              {/* Timeline connector */}
              <Box display="flex" flexDirection="column" alignItems="center" style={{ marginRight: 16, width: 24 }}>
                <Box style={{
                  width: 12, height: 12, borderRadius: '50%',
                  backgroundColor: statusColor(r.generation_status, r.is_error),
                  flexShrink: 0, marginTop: 4,
                }} />
                {i < runs.length - 1 && (
                  <Box style={{ flex: 1, width: 2, backgroundColor: '#E0E0E0', marginTop: 4 }} />
                )}
              </Box>
              <Paper elevation={1} style={{ flex: 1, padding: '10px 14px', cursor: 'pointer', borderLeft: r.is_error ? '3px solid #D32F2F' : undefined }}
                onClick={() => onSelectRun(r)}>
                <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <Typography variant="body2" style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                    #{r.workflow_run_number ?? r.runner_id.slice(0, 8)}
                  </Typography>
                  <StatusBadge status={r.generation_status} isError={r.is_error} />
                  {r.branch_name && (
                    <Chip label={r.branch_name} size="small" style={{ fontSize: '0.65rem', height: 18, fontFamily: 'monospace' }} />
                  )}
                  {r.pr_number && (
                    <Typography variant="caption" style={{ fontFamily: 'monospace' }}>PR #{r.pr_number}</Typography>
                  )}
                  {r.ai_cost !== null && (
                    <Typography variant="caption" style={{ fontFamily: 'monospace' }}>
                      ${Number(r.ai_cost).toFixed(4)}
                    </Typography>
                  )}
                  <Typography variant="caption" color="textSecondary" style={{ marginLeft: 'auto' }}>
                    {formatDate(r.created_at)}
                  </Typography>
                </Box>
                {r.is_error && r.error_message && (
                  <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 4, wordBreak: 'break-word' }}>
                    {r.error_step ? `[${r.error_step}] ` : ''}{r.error_message.slice(0, 200)}{r.error_message.length > 200 ? '…' : ''}
                  </Typography>
                )}
              </Paper>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
}

// ─── Stats Panel ──────────────────────────────────────────────────────────────

function StatsPanel({ baseUrl, fetchApi }: { baseUrl: string; fetchApi: any }) {
  const classes = useStyles();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);


  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchApi.fetch(`${baseUrl}/stats?days=${days}`);
      if (!res.ok) throw new Error(`Stats: HTTP ${res.status}`);
      setStats(await res.json());
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, fetchApi, days]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  if (error) return <Box py={2}><Typography color="error">{error}</Typography></Box>;
  if (!stats) return null;

  return (
    <Box>
      {/* Day filter */}
      <Box display="flex" alignItems="center" mb={3} style={{ gap: 8 }}>
        <Typography variant="body2" color="textSecondary">Time range:</Typography>
        {[7, 30, 90, 365].map(d => (
          <Chip
            key={d}
            label={`${d}d`}
            size="small"
            clickable
            onClick={() => setDays(d)}
            style={{
              fontWeight: days === d ? 700 : 400,
              backgroundColor: days === d ? '#1a73e8' : undefined,
              color: days === d ? '#fff' : undefined,
            }}
          />
        ))}
      </Box>

      {/* Summary cards */}
      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        {[
          { label: 'Total Generations', value: stats.total_runs },
          { label: 'Successful', value: stats.successful_generations, color: '#4CAF50' },
          { label: 'No Changes', value: stats.no_changes_count, color: '#FF9800' },
          { label: 'Errors', value: stats.errors_count, color: stats.errors_count > 0 ? '#F44336' : '#4CAF50' },
          { label: 'Success Rate', value: stats.success_rate, color: parseFloat(stats.success_rate) >= 70 ? '#4CAF50' : '#F44336' },
          { label: 'PRs Created', value: stats.total_prs_created },
          { label: 'Total AI Cost', value: stats.total_ai_cost !== null ? `$${stats.total_ai_cost.toFixed(3)}` : '—' },
          { label: 'Avg Cost/Run', value: stats.avg_cost_per_run !== null ? `$${stats.avg_cost_per_run.toFixed(4)}` : '—' },
        ].map(s => (
          <Grid item xs={6} sm={4} md={3} key={s.label}>
            <Paper className={classes.statCard} elevation={1}>
              <Typography className={classes.statValue} style={(s as any).color ? { color: (s as any).color } : undefined}>
                {String(s.value)}
              </Typography>
              <Typography className={classes.statLabel}>{s.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Daily trend chart */}
      <Typography className={classes.sectionTitle}>Daily Generation Trend</Typography>
      <Paper elevation={1} style={{ padding: '16px 20px', marginBottom: 24 }}>
        <DailyTrendChart baseUrl={baseUrl} fetchApi={fetchApi} days={days} />
      </Paper>

      {/* By provider */}
      {stats.by_ai_provider.length > 0 && (
        <>
          <Typography className={classes.sectionTitle}>Stats by AI Provider</Typography>
          <TableContainer component={Paper} elevation={1} style={{ marginBottom: 24 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Provider</strong></TableCell>
                  <TableCell align="right"><strong>Runs</strong></TableCell>
                  <TableCell align="right"><strong>Total Cost</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.by_ai_provider.map((p, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Chip label={p.provider} size="small" style={{ fontSize: '0.68rem', height: 18 }} />
                    </TableCell>
                    <TableCell align="right">{p.count}</TableCell>
                    <TableCell align="right">
                      {p.total_cost !== null ? `$${p.total_cost.toFixed(4)}` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* By repo */}
      {stats.by_repo.length > 0 && (
        <>
          <Typography className={classes.sectionTitle}>Stats by Repository</Typography>
          <TableContainer component={Paper} elevation={1} style={{ marginBottom: 24 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Repository</strong></TableCell>
                  <TableCell align="right"><strong>Runs</strong></TableCell>
                  <TableCell align="right"><strong>Success Rate</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.by_repo.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>{shortName(r.repo_name)}</Typography>
                    </TableCell>
                    <TableCell align="right">{r.count}</TableCell>
                    <TableCell align="right">
                      <span style={{ color: parseFloat(r.success_rate) >= 70 ? '#4CAF50' : '#F44336', fontWeight: 600 }}>
                        {r.success_rate}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Recent errors */}
      {stats.recent_errors.length > 0 && (
        <>
          <Typography className={classes.sectionTitle}>Recent Errors</Typography>
          {stats.recent_errors.map(r => (
            <Paper key={r.id} elevation={1} style={{ padding: '10px 16px', marginBottom: 8, borderLeft: '4px solid #D32F2F' }}>
              <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                <Typography variant="body2" style={{ fontWeight: 700 }}>{shortName(r.repo_name)}</Typography>
                <Chip label={r.jira_ticket_id} size="small" style={{ fontSize: '0.65rem', height: 18 }} />
                {r.error_step && (
                  <Chip label={r.error_step} size="small"
                    style={{ fontSize: '0.65rem', height: 18, backgroundColor: '#FFF3E0', color: '#E65100', border: '1px solid #FFB74D' }} />
                )}
                <Typography variant="caption" color="textSecondary" style={{ marginLeft: 'auto' }}>
                  {formatDate(r.created_at)}
                </Typography>
              </Box>
              {r.error_message && (
                <Typography variant="caption" color="error" style={{ wordBreak: 'break-word' }}>
                  {r.error_message.slice(0, 300)}{r.error_message.length > 300 ? '…' : ''}
                </Typography>
              )}
            </Paper>
          ))}
        </>
      )}
    </Box>
  );
}

// ─── Repos Panel ──────────────────────────────────────────────────────────────

function ReposPanel({
  baseUrl,
  fetchApi,
  onSelectRepo,
}: {
  baseUrl: string;
  fetchApi: any;
  onSelectRepo: (repo: string) => void;
}) {
  const classes = useStyles();
  const [repos, setRepos] = useState<RepoSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'no_changes' | 'error'>('all');
  const [confirmDeleteRepo, setConfirmDeleteRepo] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'totalRuns' | 'successCount' | 'errorCount' | 'lastRun' | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (col: typeof sortBy) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchApi.fetch(`${baseUrl}/repos`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRepos(await res.json());
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, fetchApi]);

  useEffect(() => { load(); }, [load]);

  const handleDeleteRepo = async (repoName: string) => {
    await fetchApi.fetch(`${baseUrl}/repo/${encodeURIComponent(repoName)}`, { method: 'DELETE' });
    setRepos(prev => prev.filter(r => r.repoName !== repoName));
    setConfirmDeleteRepo(null);
  };

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  if (error) return <Box py={2}><Typography color="error">{error}</Typography></Box>;

  const q = search.trim().toLowerCase();
  const filtered = repos.filter(r => {
    const matchesSearch = !q ||
      r.repoName.toLowerCase().includes(q) ||
      (r.latestBranch ?? '').toLowerCase().includes(q) ||
      (r.latestModel ?? '').toLowerCase().includes(q);
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'error' && r.latestIsError) ||
      (statusFilter !== 'error' && !r.latestIsError && r.latestStatus === statusFilter);
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    if (!sortBy) return shortName(a.repoName).localeCompare(shortName(b.repoName));
    const aVal: any = a[sortBy] ?? (sortDir === 'asc' ? Infinity : -Infinity);
    const bVal: any = b[sortBy] ?? (sortDir === 'asc' ? Infinity : -Infinity);
    if (sortBy === 'lastRun') {
      return sortDir === 'asc'
        ? new Date(aVal).getTime() - new Date(bVal).getTime()
        : new Date(bVal).getTime() - new Date(aVal).getTime();
    }
    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  });

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2} style={{ gap: 12, flexWrap: 'wrap' }}>
        <Typography className={classes.sectionTitle} style={{ marginBottom: 0, flex: '0 0 auto' }}>
          Tracked Repositories ({filtered.length}{filtered.length !== repos.length ? ` of ${repos.length}` : ''})
        </Typography>
        <Box style={{ flex: 1, minWidth: 200 }}>
          <TextField
            size="small" variant="outlined" placeholder="Search repo, branch, model…"
            value={search} onChange={e => setSearch(e.target.value)} fullWidth
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 18, color: '#999' }} /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}><ClearIcon style={{ fontSize: 16 }} /></IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
        <Box display="flex" style={{ gap: 6 }}>
          {(['all', 'success', 'no_changes', 'error'] as const).map(f => (
            <Chip key={f} label={f.replace('_', ' ')} size="small" clickable
              onClick={() => setStatusFilter(f)}
              style={{
                fontWeight: statusFilter === f ? 700 : 400,
                backgroundColor: statusFilter === f
                  ? f === 'success' ? '#4CAF50' : f === 'error' ? '#F44336' : f === 'no_changes' ? '#FF9800' : '#1a73e8'
                  : undefined,
                color: statusFilter === f ? '#fff' : undefined,
              }}
            />
          ))}
        </Box>
        <IconButton size="small" onClick={load} title="Refresh"><RefreshIcon /></IconButton>
      </Box>

      {repos.length === 0 && (
        <Paper elevation={1} style={{ padding: 32, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No repositories tracked yet. Send a POST to{' '}
            <code>/api/unit-test-generation-tracker/record</code> from your GitHub Actions workflow.
          </Typography>
        </Paper>
      )}

      {repos.length > 0 && filtered.length === 0 && (
        <Paper elevation={1} style={{ padding: 24, textAlign: 'center' }}>
          <Typography color="textSecondary">No repositories match your filters.</Typography>
        </Paper>
      )}

      {filtered.length > 0 && (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Repository</strong></TableCell>
                <TableCell sortDirection={sortBy === 'totalRuns' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'totalRuns'} direction={sortBy === 'totalRuns' ? sortDir : 'desc'} onClick={() => handleSort('totalRuns')}>
                    <strong>Runs</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'errorCount' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'errorCount'} direction={sortBy === 'errorCount' ? sortDir : 'desc'} onClick={() => handleSort('errorCount')}>
                    <strong>Errors</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell><strong>Total Cost</strong></TableCell>
                <TableCell sortDirection={sortBy === 'lastRun' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'lastRun'} direction={sortBy === 'lastRun' ? sortDir : 'desc'} onClick={() => handleSort('lastRun')}>
                    <strong>Last Run</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(r => (
                <TableRow key={r.repoName} className={classes.tableRow} onClick={() => onSelectRepo(r.repoName)}>
                  <TableCell>
                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                      {r.latestIsError && (
                        <Tooltip title="Active error — last run failed">
                          <WarningIcon className={classes.alarmPulse} style={{ fontSize: 16, color: '#D32F2F' }} />
                        </Tooltip>
                      )}
                      <Typography variant="body2" style={{ fontWeight: 600, color: '#0469E3' }}>
                        {shortName(r.repoName)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{r.totalRuns}</TableCell>
                  <TableCell>
                    <span style={{ color: r.errorCount > 0 ? '#F44336' : undefined, fontWeight: r.errorCount > 0 ? 700 : 400 }}>
                      {r.errorCount}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                      {r.totalCost !== null ? `$${r.totalCost.toFixed(4)}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary" style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(r.lastRun)}
                    </Typography>
                  </TableCell>
                  <TableCell padding="none" onClick={e => e.stopPropagation()}>
                    {confirmDeleteRepo === r.repoName ? (
                      <Box display="flex" alignItems="center" style={{ gap: 4, whiteSpace: 'nowrap' }}>
                        <Button size="small" variant="contained"
                          style={{ backgroundColor: '#D32F2F', color: '#fff', fontSize: '0.65rem', padding: '2px 6px', minWidth: 0 }}
                          onClick={() => handleDeleteRepo(r.repoName)}>
                          Delete
                        </Button>
                        <Button size="small" style={{ fontSize: '0.65rem', padding: '2px 6px', minWidth: 0 }}
                          onClick={() => setConfirmDeleteRepo(null)}>
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Tooltip title="Delete repo and all its runs">
                        <IconButton size="small" onClick={() => setConfirmDeleteRepo(r.repoName)}>
                          <DeleteIcon style={{ fontSize: 16, color: '#D32F2F' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ─── API Docs Panel ───────────────────────────────────────────────────────────

function ApiDocsPanel({ baseUrl }: { baseUrl: string }) {
  const endpoint = baseUrl.replace(/\/api\/.*/, '') + '/api/unit-test-generation-tracker';
  return (
    <Box>
      <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 16 }}>Webhook Integration</Typography>
      <Paper elevation={1} style={{ padding: 20, marginBottom: 16 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
          POST {endpoint}/record
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          Call this endpoint from your GitHub Actions workflow after running the GenerateUnitTestCases action.
          Only GitHub Actions runner IPs and <code>sf-portal.sourcef.us</code> are allowed.
        </Typography>
        <Divider style={{ marginBottom: 12 }} />
        <Typography variant="caption" style={{ fontFamily: 'monospace', display: 'block' }}>
          <strong>Required:</strong> repo_name, runner_id, jira_ticket_id, generation_status
        </Typography>
        <Typography variant="caption" style={{ fontFamily: 'monospace', display: 'block', marginTop: 4 }}>
          <strong>Optional:</strong> workflow_run_number, has_changes, branch_name, branch_url, pr_number,
          pr_url, pr_action, target_branch, model_name, ai_provider, ai_cost, confluence_url,
          pr_creator, run_timestamp, is_error, error_message, error_step, job_status
        </Typography>
      </Paper>
      <Paper elevation={1} style={{ padding: 20 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
          Example GitHub Actions step
        </Typography>
        <Box component="pre" style={{ background: '#F5F5F5', borderRadius: 6, padding: 16, fontSize: '0.75rem', overflowX: 'auto', margin: 0 }}>
{`- name: Report to Backstage Unit Test Generation Tracker
  if: always()
  run: |
    curl -s --max-time 10 -X POST \\
      "${endpoint}/record" \\
      -H "Content-Type: application/json" \\
      -d '{
        "repo_name": "$\{{ github.repository }}",
        "runner_id": "$\{{ github.run_id }}",
        "workflow_run_number": $\{{ github.run_number }},
        "jira_ticket_id": "$\{{ inputs.jira_ticket_id }}",
        "generation_status": "success",
        "has_changes": true,
        "branch_name": "$\{{ steps.commit_tests.outputs.branch_name }}",
        "branch_url": "$\{{ steps.commit_tests.outputs.branch_url }}",
        "pr_number": $\{{ steps.create_pr.outputs.pr_number || 0 }},
        "pr_url": "$\{{ steps.create_pr.outputs.pr_url }}",
        "pr_action": "$\{{ steps.create_pr.outputs.pr_action }}",
        "model_name": "claude-3-5-sonnet",
        "ai_provider": "aws_bedrock",
        "ai_cost": "$\{{ env.TOTAL_CLAUDE_COST }}",
        "pr_creator": "$\{{ github.event.pull_request.user.login }}",
        "target_branch": "$\{{ inputs.target_branch }}"
      }' || echo "Backstage report failed (non-critical)"`}
        </Box>
      </Paper>
    </Box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export const UnitTestGenerationPage = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [baseUrl, setBaseUrl] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  // Navigation state via URL params
  const view = searchParams.get('view'); // 'repo' | 'jira' | 'run'
  const viewParam = searchParams.get('param');

  const [selectedRun, setSelectedRun] = useState<RunRecord | null>(null);

  const navigateTo = (v: string, param: string) => {
    setSearchParams({ view: v, param });
    setSelectedRun(null);
  };

  const goBack = () => {
    setSearchParams({});
    setSelectedRun(null);
  };

  useEffect(() => {
    discoveryApi.getBaseUrl('unit-test-generation-tracker').then(setBaseUrl);
  }, [discoveryApi]);

  if (!baseUrl) {
    return (
      <Page themeId="tool">
        <Header title="Unit Test Generation" subtitle="AI-powered unit test generation tracker" />
        <Content>
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        </Content>
      </Page>
    );
  }

  const renderContent = () => {
    // Run detail
    if (selectedRun) {
      return (
        <RunDetailView
          run={selectedRun}
          onBack={view === 'repo' && viewParam ? () => { setSelectedRun(null); } : goBack}
          baseUrl={baseUrl}
          fetchApi={fetchApi}
        />
      );
    }

    // Repo view
    if (view === 'repo' && viewParam) {
      return (
        <RepoHistoryPanel
          repoName={viewParam}
          baseUrl={baseUrl}
          fetchApi={fetchApi}
          onBack={goBack}
          onSelectRun={r => setSelectedRun(r)}
        />
      );
    }

    // Jira view
    if (view === 'jira' && viewParam) {
      return (
        <JiraTicketView
          ticketId={viewParam}
          baseUrl={baseUrl}
          fetchApi={fetchApi}
          onBack={goBack}
          onSelectRun={r => setSelectedRun(r)}
        />
      );
    }

    // Main dashboard
    return (
      <Box>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginBottom: 24 }}
        >
          <Tab label="Repositories" icon={<StorageIcon />} />
          <Tab label="Statistics" icon={<AssessmentIcon />} />
          <Tab label="API / Integration" icon={<CodeIcon />} />
        </Tabs>
        {tab === 0 && <ReposPanel baseUrl={baseUrl} fetchApi={fetchApi} onSelectRepo={r => navigateTo('repo', r)} />}
        {tab === 1 && <StatsPanel baseUrl={baseUrl} fetchApi={fetchApi} />}
        {tab === 2 && <ApiDocsPanel baseUrl={baseUrl} />}
      </Box>
    );
  };

  return (
    <Page themeId="tool">
      <Header
        title="Unit Test Generation"
        subtitle="Monitor AI-powered unit test generation runs from GitHub Actions"
      />
      <Content>
        {renderContent()}
      </Content>
    </Page>
  );
};
